import type { PersonView } from "@beacon/core/access"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Outlet, useLocation, useNavigate, useOutletContext } from "react-router"
import {
  PageScroll,
  PeopleDirectory,
  PhotoDirectory,
  useKeyboardInset,
  useViewport,
  type PageScrollHandle,
} from "@/design-system/index.ts"
import { peopleQuery } from "@/people/client.ts"
import { SidebarInset, SidebarProvider } from "@/primitives/sidebar.tsx"
import { photosQuery } from "@/profile/client.ts"
import { forgetAllDrafts, settleDrafts } from "@/chat/drafts.ts"
import { ChatLiveBanner, ChatLiveHost } from "@/chat/live.tsx"
import { dropPushHere, forgetPushHere } from "@/chat/push-device.ts"
import { ChatPushHost } from "@/chat/push-settings.tsx"
import { ConnectionBanner } from "@/installed-app/connection.tsx"
import { InstallOfferSheet } from "@/installed-app/install-offer.tsx"
import { assumeOnline, waitingForNetwork } from "@/installed-app/offline.ts"
import { checkShows } from "@/profile/describe.ts"
import { ProfileCheck } from "@/profile/profile-check.tsx"
import { myTicketsQuery, projectsQuery } from "@/projects/client.ts"
import { SignInScreen } from "@/sign-in/sign-in-screen.tsx"
import { meQuery, signOut, type Me } from "@/sign-in/client.ts"
import { useShortcuts } from "@/shortcuts/use-shortcut.ts"
import { AppSidebar } from "./app-sidebar.tsx"
import { CommandPalette, CommandPaletteContext } from "./command-palette.tsx"
import { ShellTabBar } from "./shell-tab-bar.tsx"
import { adminDestinations, currentTabOf, destinations } from "./destinations.ts"
import { ShellError } from "./route-error.tsx"
import { ShellSkeleton } from "./shell-skeleton.tsx"
import { ShortcutsDialog } from "./shortcuts-dialog.tsx"

/*
 * The shell every page renders in, and the guard in front of it. Beacon first asks who is signed
 * in: while it asks, the shell's empty frame; if nobody is, the sign-in card, at whatever address
 * was opened, and back to it afterwards; once someone is, the shell.
 *
 * Opened with no network, it can't ask, and says "You're offline" on the canvas until the network
 * is back, or until the Member says to try anyway.
 *
 * The shell is the sidebar on the canvas and, beside it, the inset panel that is the page: one
 * scroll container, in which each page renders its own PageHeader first. On the compact layout the
 * panel runs to the screen's edges instead, clear of the status bar, with the tab bar under it
 * (`ShellTabBar`) and the sidebar as the avatar's drawer (`AppSidebar` renders it). Either way it
 * is the same panel in the same place, so a tablet turning across 1024px keeps the page it shows.
 * The command palette, the Shortcuts dialog and the keys that open them and move between pages live
 * here, for every page, and so do every Member's photos, which each avatar reads, the once-only
 * check of the signed-in Member's seeded Profile, the banner atop the panel while the network is
 * gone, Chat's live socket (and its banner on a Chat page while it reconnects), and the sheet "Install Beacon" opens where the browser has no prompt of its own.
 * Chat's drafts on the device are the signed-in Member's alone: another's go as they sign in, and
 * theirs go as they sign out here.
 */

/** Who is signed in. Every page under the shell has someone; the guard shows no page otherwise. */
export type Session = { status: "signed-in"; me: Me }

type LayoutContext = { session: Session; signOut: () => Promise<void> }

/** Who is signed in, as the shell found out once for every page under it. */
export function useSession() {
  return useOutletContext<LayoutContext>()
}

// The sidebar opens with the page only where the widest table still fits beside it: 15rem of
// sidebar and a 56rem table need 1280px. Narrower than that it starts collapsed to its icons, so a
// desk table fits down to 1024px, below which the compact layout takes over, and it can still be
// opened by hand.
const opensExpanded = () => window.matchMedia("(min-width: 1280px)").matches

export function Layout() {
  const me = useQuery(meQuery)
  const { pathname, search, hash } = useLocation()
  const { compact } = useViewport()
  // Nobody signed in, as the web API says (a session lapsed, or its Member deactivated): what
  // anyone left unsent in Chat on this device goes, and its push subscription, as signing out here
  // takes them.
  const nobody = me.data === null
  useEffect(() => {
    if (!nobody) return
    forgetAllDrafts()
    void dropPushHere()
  }, [nobody])

  if (waitingForNetwork(me)) return <ShellError refusal="offline" onRetry={assumeOnline} />
  if (me.isPending) return <ShellSkeleton compact={compact} expanded={opensExpanded()} />
  if (me.isError) return <ShellError onRetry={() => void me.refetch()} />
  if (!me.data) return <SignInScreen next={`${pathname}${search}${hash}`} />
  return <Shell me={me.data} />
}

/**
 * The G-chords: G then a letter goes to a page. The Admin pages' chords are bound only for an
 * Admin, as only an Admin has those pages.
 */
function useGoToChords(admin: boolean) {
  const navigate = useNavigate()
  const chords = (list: typeof destinations) =>
    Object.fromEntries(list.map(({ to, chord }) => [chord, () => void navigate(to)]))
  useShortcuts(chords(destinations))
  useShortcuts(chords(adminDestinations), { enabled: admin })
}

/** No photos, kept as one object so the directory doesn't change while they load. */
const noPhotos: Readonly<Record<string, string>> = {}

/** Nobody, kept as one list so the palette's people don't change while they load. */
const nobody: readonly PersonView[] = []

function Shell({ me }: { me: Me }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  const page = useRef<PageScrollHandle>(null)
  const { compact } = useViewport()
  const { pathname } = useLocation()
  const admin = me.role === "admin"
  const projects = useQuery(projectsQuery(false)).data ?? []
  const openTickets = useQuery({
    ...myTicketsQuery(false, false),
    select: (groups) => groups.reduce((count, { tickets }) => count + tickets.length, 0),
  }).data
  const photos = useQuery(photosQuery).data ?? noPhotos
  const people = useQuery(peopleQuery).data
  const cards = useMemo(
    () => Object.fromEntries((people ?? []).map((person) => [person.id, person])),
    [people],
  )
  // How far each person's time is from yours: by your Profile's time zone, else this browser's.
  const viewerZone =
    people?.find(({ id }) => id === me.id)?.timeZone ??
    Intl.DateTimeFormat().resolvedOptions().timeZone

  // Everything cached was read as the Member signing out, so none of it outlives their session:
  // resetting drops it all and asks again, which finds nobody signed in, and the guard shows the
  // sign-in card at the root. What they left unsent in Chat on this device goes first.
  const signOutHere = useCallback(async () => {
    forgetAllDrafts()
    // While the session still holds, so the server forgets this browser along with it.
    await forgetPushHere().catch(() => undefined)
    await signOut()
    await navigate("/")
    await queryClient.resetQueries()
  }, [navigate, queryClient])

  // Whoever signs in here finds only their own Chat drafts on the device, never another Member's.
  useEffect(() => settleDrafts(me.id), [me.id])

  const openPalette = useCallback(() => setPaletteOpen(true), [])
  const showShortcuts = useCallback(() => setShortcutsOpen(true), [])
  const palette = useMemo(() => ({ openPalette }), [openPalette])
  const outlet = useMemo(
    () => ({ session: { status: "signed-in", me }, signOut: signOutHere }) satisfies LayoutContext,
    [me, signOutHere],
  )

  // ⌘B is the sidebar's own (`AppSidebar` binds it); binding it here too would toggle twice. `/` is
  // the page's when it has a search field, so the shell binds it as a fallback: a fresh load mounts
  // the page first, and mounting later must not win it. Elsewhere it opens the palette.
  useShortcuts({
    "command-palette": openPalette,
    shortcuts: showShortcuts,
  })
  useShortcuts({ search: openPalette }, { fallback: true })
  useGoToChords(admin)
  useKeyboardInset()

  return (
    <PhotoDirectory photos={photos}>
      <PeopleDirectory cards={cards} viewerZone={viewerZone}>
        <CommandPaletteContext value={palette}>
          <SidebarProvider defaultOpen={opensExpanded()} className="compact:h-dvh compact:flex-col">
            <AppSidebar
              me={me}
              projects={projects}
              openTickets={openTickets}
              onSearch={openPalette}
              onShowShortcuts={showShortcuts}
              onSignOut={() => void signOutHere()}
            />
            {/* Clipped on compact as the desk's inset panel is, so a page's visually hidden text,
                placed against the panel rather than the scroll container, never makes the
                document itself scroll and drag the tab bar up the screen. */}
            <SidebarInset className="compact:min-h-0 compact:overflow-clip compact:px-safe compact:pt-safe">
              <ConnectionBanner />
              <ChatLiveBanner />
              {/* A tab's own page has no back in its compact header: the tab bar is how to leave. */}
              <PageScroll ref={page} tabRoot={currentTabOf(pathname) === pathname}>
                <Outlet context={outlet} />
              </PageScroll>
            </SidebarInset>
            {/* After the page, so a screen reader and Tab meet the page before the tabs. */}
            {compact && (
              <ShellTabBar me={me} openTickets={openTickets} onSearch={openPalette} page={page} />
            )}
            <CommandPalette
              open={paletteOpen}
              onOpenChange={setPaletteOpen}
              admin={admin}
              projects={projects}
              people={people ?? nobody}
              onShowShortcuts={showShortcuts}
              onSignOut={() => void signOutHere()}
            />
            <ShortcutsDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} admin={admin} />
            <InstallOfferSheet />
            <ChatLiveHost me={me.id} />
            <ChatPushHost />
            {checkShows(me) && <ProfileCheck me={me} />}
          </SidebarProvider>
        </CommandPaletteContext>
      </PeopleDirectory>
    </PhotoDirectory>
  )
}
